// Generic reports UI code:

// {{{ Reports.* utilities

Reports = {
	reports: [],
	
	registerReport(report) {
		this.reports.push(report);
	},
	
	getById(id) {
		return this.reports.find(r => r.id === id);
	},

	getDefault() {
		return this.reports[0];
	},
};

Reports.CellFormats = {
	/*
	 * - labelFormat: formatting function for label, or use constant 'allAsPercentWithLabels' to
	 *                render list of percentages delimited by | with title with all labels
	 * - groups: doughnut graph groups
	 *   - label
	 *   - color
	 *   - dataProperty
	 */
	makeDoughnut(labelFormat, groups) {
		return (td, v, row) => {
			
			const numVal = g => (g.data ? g.data(row) : row[g.dataProperty]) | 0; // numeric value or 0 as fallback

			let label = null;
			
			// determine label:
			if (labelFormat === 'allAsPercentWithLabels') {
				// special case: render label as percents delimited by | with title containing all percents with labels

				const total = groups.map(numVal).reduce((a, b) => a + b);
				
				if (total > 0) {
					
					const percent = g => Formats.percent(numVal(g) / total);
					
					td.setAttribute('title', groups
						.map(g => `${percent(g)} (${g['label']})`)
						.join('\n'))

					label = groups.map(percent).insertBetweenAll({
						nodeName: 'span',
						className: 'text-separator',
						textContent: " | "
					});
				}

			} else {
				// all other cases
				// assume labelFormat is function and call it
				label = labelFormat(td, v, row);
			}
			
			return Charts.makeDoughnut(td, groups.map(g => ({
				label: g.label,
				color: g.color,
				value: numVal(g),
			})), label);
		};
	},
};

Reports.Filters = {
	options: (name, items) => ({
		ref: name,
		xtype: 'Widgets.ButtonSwitcher',
		cls: "button-switcher small light",
		items: items,
		'on:beforeselect'(el, i) {
			el.setSelected(i.id);
			this.fire('change', i.id);
		},
		
		toParams(params) {
			params.set(name, this.getSelected());
		},
		
		fromParams(params) {
			this.setSelected(params.get(name));
		},
		
		toQuery(query) {
			query[name] = this.getSelected();
		}
	}),

	daterange: name => ({
		ref: name,
		xtype: 'Widgets.DateRangeInput',
		
		// 'on:change'

		toQuery(query) {
			let [since, until] = this.getTimeRange();
			query[name + '_since'] = since || '';
			query[name + '_until'] = until || '';
		},

		async fromParams(params) {
			let range = params.get(name + '_range') || 'today';

			await this.setRangeOption(range);

			if (range === 'custom') {
				this.setTimeRange(
					params.get(name + '_since'),
					params.get(name + '_until')
				);
			}
		},

		toParams(params) {
			let range = this.getRange();
			if (range === 'custom') {
				let [since, until] = this.getTimeRange();
				params.set(name + '_since', since || '');
				params.set(name + '_until', until || '');
			} else {
				params.delete(name + '_since');
				params.delete(name + '_until');
			}
			params.set(name + '_range', range);
		}
	}),
}

Reports.Utils = {
	timesBoundariesColumns(prefix) {
		return env.REPORTS_TIME_BOUNDARIES_SECONDS.flatMap((s, i, arr) => {
			const cols = [];
			cols.push({
				id: prefix + '_lt' + s,
				name: _('<{s}s').replace('{s}', s),
			});
			if (i === arr.length - 1) { // last item
				cols.push({
					id: prefix + '_gt' + s,
					name: _('>{s}s').replace('{s}', s),
				});
			}
			return cols;
		});
	}
}

// }}}
// {{{ Reports.OverviewScreen

GW.define('Reports.OverviewScreen', 'Main.Screen', {
	title: _('Reporty'),
	titleIcon: 'chart',

	'after:initComponent'() {
		this.bodyEl.gwReplaceChildren([{
			className: 'tab-switcher',
			xtype: 'Widgets.TabSwitcher',
			ref: 'tabs',
			items: Reports.reports.map(report => ({
				id: report.id,
				title: report.name,
				href: APP_BASE + 'reports/' + report.id,
			})),
		}, {
			className: 'reports-report',
			xtype: 'Widgets.CardSwitcher',
			ref: 'cards',
		}], this);
	},

	async applyParams(params) {
		const report = Reports.getById(params.selectedReportId) || Reports.getDefault();
		
		if (!report) {
			console.warn("Could not load report " + params.selectedReportId);
			return;
		}
		
		const user = Application.session.user.id;

		let savedParams = Utils.getLocalState({user, report: report.id, key: 'report-params'});

		let reportParams;
		if (savedParams) {
			// load saved params
			reportParams = new URLSearchParams(savedParams);
			
			if (((reportParams.get('time_range') || 'custom') === 'custom') && 
				!reportParams.get('time_since') && !reportParams.get('time_until')
			) {
				// reset saved params if the saved value is an unbound custom interval (old default filter, which should
				// not be default anymore due to performance reasons
				console.log(
					"Saved report filter of report " + report.id + " uses custom range with unbound interval, " +
					"the filter will be reset to default"
				);
				reportParams = null;
			}
		} 
		
		if (!reportParams) {
			// default filter params 
			reportParams = new URLSearchParams(location.search);
		}
		

		// update URL params from saved ones
		Application.route({
			resolve: false,
			paramsFn: () => reportParams,
		});
		
		this.tabs.setSelected(report.id);

		const c = await this.cards.show({
			id: report.id,
			xtype: 'Reports.ReportScreen',
			params: reportParams,
			reportId: report.id,
			...report.render(),
			'on:paramsUpdated': () => {
				// save params
				Utils.setLocalState({user, report: report.id, key: 'report-params'}, location.search);
			},
			refs: {},
		});
	},
});

// }}}
// {{{ Reports.ReportScreen

GW.define('Reports.ReportScreen', 'GW.Component', {
	reportId: null,
	expandable: false,
	
	firstDataLoaded: false,

	refs: {},
	filterRefs: {},
	
	// current active view
	view: null,
	// columns of data of current active view
	viewColumns: [],
	/**
	 * data of current active view
	 * @type {{columns: array, rows: array}|null}
	 */
	viewData: null,

	views: null,

	initComponent() {
		this.el = document.gwCreateElement({
			children: [{
				nodeName: 'form',
				'on:submit': ev => {
					ev.preventDefault();
					this.reloadData(true);
				},
				className: 'toolbar',
				children: [
					{
						ref: 'filters',
						className: 'filters',
					},
					{
						nodeName: 'button',
						type: 'submit',
						className: 'primary small',
						textContent: _('Zobrazit'),
					},
					{
						nodeName: 'button',
						type: 'button',
						className: 'secondary small',
						textContent: _('Exportovat XLSX'),
						"on:click": () => {
							let params = new URLSearchParams({
								filter: JSON.stringify(this.buildFilterData('xlsx')),
							});

							let group = Application.getActiveUserGroup();
							if (group) {
								params.set('active_user_group', group.id);
							}

							location.href = API_BASE + 'reports/' + this.reportId + '/export/xlsx?' + params.toString();
						},
					}, {
						':skip': !this.expandable,
						ref: 'toggleAllGroupsBtn',
						nodeName: 'button',
						type: 'button',
						className: 'secondary small disabled',
						textContent: _('Rozbalit vše'),
						"on:click": () => this.viewData && this.refs.table.toggleAllGroups(),
					},
				],
			}, {
				ref: 'contentEl',
				className: 'report-content',
				children: [{
					className: 'report-views-row',
					children: [{
						ref: 'views',
						className: 'report-views',
						// rendered in reloadViews()
					}, {
						ref: 'viewsFilters',
						className: 'report-filter',
					}]
				}, {
					ref: 'table',
					xtype: this.expandable ? 'ExpandableSmartTable' : 'SmartTable',
					extraClass: 'report-table panel-container no-padding',
					getColumns: () => this.viewColumns,
					getGroupColumn: () => this.groupColumn || this.viewColumns[0].id,
					'after:renderRow': (tr, row) => this.renderTableRow(tr, row),
					'on:expand': (table) => {
						this.refs.toggleAllGroupsBtn.textContent = this.refs.table.allExpanded() ? 
							_('Sbalit vše') : _('Rozbalit vše');
						
						this.saveExpandedState([...table.expanded]);
					}, 
				}, {
					ref: 'chart',
					className: 'report-chart panel-container',
				}, {
					ref: 'altContent',
					className: 'report-alt-content hidden',
				}]
			}],
		}, this.refs);
		this.refs.table.el.classList.add('no-header');
		this.refs.table.msgEl.gwReplaceChildren([{
			textContent: _('Data nejsou načtena'),
		}, {
			nodeName: 'button',
			type: 'button',
			className: 'button primary',
			textContent: _('Zobrazit'),
			'on:click': () => {
				this.reloadData(true);
			}
		}]);
		this.reloadViews();
	},
	
	setFilters(container, filterEls, reloadOnChange) {
		container.gwReplaceChildren(filterEls.map(el => ({
			...el,
			'on:change': () => {
				this.updateParams();
				if (reloadOnChange) {
					this.reloadData();
				}
			},
		})), this.filterRefs);
	},
	
	saveExpandedState(expanded) {
		const user = Application.session.user.id;
		Utils.setLocalState({user, report: this.reportId, view: this.view, key: 'reports-expanded'}, expanded);
	},
	
	loadExpandedState() {
		const user = Application.session.user.id;
		return Utils.getLocalState({user, report: this.reportId, view: this.view, key: 'reports-expanded'});
	},

	/**
	 * to be defined in inherited component
	 * @param view currently selected view
	 * @param serverColumns columns returned from server, can be used or overridden to render custom columns in FE
	 */
	getTableColumns(view, serverColumns) {
		return serverColumns;
	},

	// to be defined in inherited component
	renderChart(el, data, view, args) {
		
	},

	// to be defined in inherited component
	renderTableRow(tr, row) {
		
	},
	
	// can be overridden in inherited component
	/**
	 * return component like object, with setSelected() property
	 */
	renderViews(views, onSelected, refs) {
		return {
			xtype: 'Widgets.ButtonSwitcher',
			cls: "button-switcher small light",
			items: views,
			'on:beforeselect': (c, i) => {
				onSelected(i.id)
			},
		};
	},

	async reloadViews() {
		const signal = Utils.abortAndRetry(this, 'reload-views');
		const views = this.views = await REST.GET('reports/' + this.reportId + '/views', null, {signal});

		if (views.length > 1) {
			this.refs.views.gwReplaceChildren([{
				ref: 'tabsViews',
				...this.renderViews(views, viewId => {
					Application.route({
						paramsFn: params => {
							params.set('view', viewId);
							return params;
						},
					});
					this.fire('paramsUpdated');
				}, this.refs)
			}], this.refs);
		} else {
			this.view = this.views[0].id;
			this.refs.views.gwReplaceChildren([]);
		}

		this.updateSelectedView();
	},

	async reloadData(triggeredByUser) {
		if (!triggeredByUser && !this.firstDataLoaded) {
			return;
		}
		this.firstDataLoaded = true;
		let signal = Utils.abortAndRetry(this, 'reload');
		let filter = this.buildFilterData();

		this.viewData = await REST.POST('reports/' + this.reportId + '/views/' + this.view + '/data', filter, {signal})
			.then(data => this.dataLoaded(data, this.view));

		this.updateTableData();
	},
	
	dataLoaded(data, view) {
		return data;
	},
	
	updateTableData() {
		if (this.viewData) {

			this.viewColumns = this.getTableColumns(this.view, this.viewData.columns);

			const expanded = this.expandable && this.loadExpandedState();
			this.refs.table.setData(this.viewData.rows, expanded);
			this.renderChart(this.refs.chart, this.viewData, this.view);

			if (this.refs.toggleAllGroupsBtn) {
				this.refs.toggleAllGroupsBtn.classList.toggle('disabled', this.refs.table.allGroups.size == 0);
			}
			
			// reset empty text content to default on first load
			this.refs.table.msgEl.textContent = this.refs.table.emptyText;
			this.refs.table.el.classList.remove('no-header');
		}
	},

	updateSelectedView(params) {
		if (this.views && this.views.length > 0 && this.refs.tabsViews) {
			params = params || new URLSearchParams(location.search);
			this.view = params.get('view') || this.views[0].id;
			this.refs.tabsViews.setSelected(this.view);
			this.reloadData(false);
		}
	},
	
	setupFilters() {
		this.setFilters(this.refs.filters, [Reports.Filters.daterange('time')]);
	},

	async applyParams(params) {
		this.filterRefs = {}; // remove references to old filter elements
		this.setupFilters();
		await Promise.all(Object.entries(this.filterRefs).map(([name, el]) => {
			el.fromParams(params);
		}));
		this.updateSelectedView(params);
	},

	buildFilterData(target = 'view') {
		let data = {};
		Object.entries(this.filterRefs).forEach(([name, el]) => {
			el.toQuery(data);
		});
		return data;
	},

	/*
	 * Update URL query parameters from the filter values.
	 */
	updateParams() {
		Application.route({
			resolve: false,
			paramsFn: params => {
				Object.entries(this.filterRefs).forEach(([name, el]) => {
					el.toParams(params);
				});
				return params;
			},
		});
		this.fire('paramsUpdated');
	},
});

// }}}

// Individual reports:

// {{{ Report: core.incomming-queue-calls-per-agent

Reports.registerReport({
	id: 'core.incomming-queue-calls-per-agent',
	name: _('Report agentů'),

	render: () => ({
		
		expandable: true,

		getTableColumns(view) {
			return [view === 'user_name' ? {
				id: 'user_name',
				name: _('Jméno'),
				formatCell(td, v, row) {
					if (row._expandable.topLevel) {
						td.gwReplaceChildren([{
							textContent: row['user_name']
						}]);
					} else {
						td.gwReplaceChildren([{
							':skip': !row['queue_name'],
							className: 'queue-name',
							textContent: row['queue_name'],
						}, {
							className: 'queue',
							textContent: row['queue'], 
						}])
					}
				},
			} : {
				id: 'extension',
				name: _('Linka'),
				formatCell(td, v, row) {
					if (row._expandable.topLevel) {
						td.gwReplaceChildren([{
							textContent: row['extension']
						}]);
					} else {
						td.gwReplaceChildren([{
							':skip': !row['queue_name'],
							className: 'queue-name',
							textContent: row['queue_name'],
						}, {
							className: 'queue',
							textContent: row['queue'], 
						}])
					}
				},
			}, {
				id: 'count',
				name: _('Celkem'),
			}, {
				id: 'count_connected',
				name: _('Dovolané'),
			},
			...Reports.Utils.timesBoundariesColumns('count_connected'),
			{
				id: 'count_not_connected',
				name: _('Nedovolané'),
			},
			...Reports.Utils.timesBoundariesColumns('count_not_connected'),
			{
				id: 'avg_ring_duration',
				format: 'duration',
				name: _('⌀ Délka vyzvánění'),
			}, {
				id: 'avg_duration',
				format: 'duration',
				name: _('⌀ Délka hovoru'),
			}, {
				id: 'sum_duration',
				format: 'duration',
				name: _('∑ Délka hovorů'),
			}, {
				id: 'service_level_ratio',
				name: _('Úroveň služby'),
				formatCell: Reports.CellFormats.makeDoughnut(
					(td, v) => Formats.percent(v),
					[{
						label: _('Dovolané'),
						dataProperty: 'count_connected',
						color: 'var(--graph-success-color, green)',
					}, {
						label: _('Nedovolané'),
						dataProperty: 'count_not_connected',
						color: 'var(--graph-error-color, red)',
					}],
				),
			}];
		},
	}),
});

// }}}
// {{{ Report: core.incomming-queue-calls-per-queue

Reports.registerReport({
	id: 'core.incomming-queue-calls-per-queue',
	name: _('Report front'),

	render: () => ({

		expandable: true,

		getTableColumns() {
			return [{
				id: 'queue',
				name: _('Fronta'),
				formatCell(td, v, row) {
					if (row._expandable.topLevel) {
						td.gwReplaceChildren([{
							':skip': !row['queue_name'],
							className: 'queue-name',
							textContent: row['queue_name'],
						}, {
							className: 'queue',
							textContent: row['queue'], 
						}])
					} else {
						td.textContent = row['user_name'];
					}
				},
			}, {
				id: 'count',
				name: _('Celkem'),
			}, {
				id: 'count_connected',
				name: _('Dovolané'),
			},
			...Reports.Utils.timesBoundariesColumns('count_connected'),
			{
				id: 'count_not_connected',
				name: _('Nedovolané'),
			},
			...Reports.Utils.timesBoundariesColumns('count_not_connected'),
			{
				id: 'avg_early_duration',
				format: 'duration',
				name: _('⌀ Délka v IVR'),
			}, {
				id: 'avg_ring_duration',
				format: 'duration',
				name: _('⌀ Délka vyzvánění'),
			}, {
				id: 'avg_duration',
				format: 'duration',
				name: _('⌀ Délka hovoru'),
			}, {
				id: 'sum_duration',
				format: 'duration',
				name: _('∑ Délka hovorů'),
			}, {
				id: 'service_level_ratio',
				name: _('Úroveň služby'),
				formatCell: Reports.CellFormats.makeDoughnut(
					(td, v) => Formats.percent(v),
					[{
						label: _('Dovolané'),
						dataProperty: 'count_connected',
						color: 'var(--graph-success-color, green)',
					}, {
						label: _('Nedovolané'),
						dataProperty: 'count_not_connected',
						color: 'var(--graph-error-color, red)',
					}]
				),
			}];
		},
	}),
});

// }}}
// {{{ Report: core.callback-cases

Reports.registerReport({
	id: 'core.callback-cases',
	name: _('Report callbacků'),

	render: () => ({

		expandable: true,

		getTableColumns(view) {
			return [!view || view === 'agent' ? {
				id: 'user_id',
				name: _('Agent'),
				formatCell(td, v, row) {
					if (row._expandable.topLevel) {
						td.textContent = row['user_name'];
					} else {
						td.textContent = row['queue'];
					}
				},
			} : {
				id: 'queue',
				name: _('Fronta'),
				formatCell(td, v, row) {
					if (row._expandable.topLevel) {
						td.textContent = row['queue'];
					} else {
						td.textContent = row['user_name'];
					}
				},
			}, {
				id: 'missed_calls_count',
				name: _('Celkem zmeškaných hovorů'),
			}, {
				id: 'missed_cases_count',
				name: _('Celkem zmeškaných případů'),
			}, {
				id: 'calls_count',
				name: _('Počet zavolání'),
			},{
				id: 'result_success_count',
				name: _('Počet pozitivních zakončení'),
			},{
				id: 'result_fail_count',
				name: _('Počet negativních zakončení'),
			},{
				id: 'not_closed_count',
				name: _('Počet nezakončených případů'),
			},{
				id: 'first_reaction_duration_avg',
				name: _('Průměrná doba první reakce'),
				format: 'duration',
			},{
				id: 'first_reaction_duration_min',
				name: _('Nejrychlejší doba první reakce'),
				format: 'duration',
			},{
				id: 'first_reaction_duration_max',
				name: _('Nejpomalejší doba první reakce'),
				format: 'duration',
			},{
				id: 'duration_avg',
				name: _('Průměrná doba uzavření'),
				format: 'duration',
			},{
				id: 'duration_min',
				name: _('Nejrychlejší doba uzavření'),
				format: 'duration',
			},{
				id: 'duration_max',
				name: _('Nejpomalejší doba uzavření'),
				format: 'duration',
			},{
				id: 'cases_missed_calls_share',
				name: _('Případy vs. Hovory'),
				formatCell: Reports.CellFormats.makeDoughnut(
					'allAsPercentWithLabels',
					[{
						label: _('Zmeškané případy'),
						dataProperty: 'missed_cases_count',
						color: 'var(--graph-success-color, green)',
					}, {
						label: _('Zmeškané hovory'),
						data: row => row['missed_calls_count'] - row['missed_cases_count'],
						color: 'var(--graph-error-color, red)',
					}],
				),
			},{
				id: 'success_result_share',
				name: _('Graf úspěšnosti'),
				formatCell: Reports.CellFormats.makeDoughnut(
					'allAsPercentWithLabels',
					[{
						label: _('Pozitivní uzavření'),
						dataProperty: 'result_success_count',
						color: 'var(--graph-success-color, green)',
					}, {
						label: _('Negativní uzavření'),
						dataProperty: 'result_fail_count',
						color: 'var(--graph-error-color, red)',
					}],
				),
			},{
				id: 'close_result_share',
				name: _('Graf uzavření případů'),
				formatCell: Reports.CellFormats.makeDoughnut(
					'allAsPercentWithLabels',
					[{
						label: _('Neuzavřené případy'),
						dataProperty: 'not_closed_count',
						color: 'var(--graph-error-color, red)',
					}, {
						label: _('Uzavřené případy'),
						data: row => row['not_closed_count'] == null ? NaN : parseInt(row['result_fail_count']) + parseInt(row['result_success_count']),
						color: 'var(--graph-success-color, green)',
					}],
				),
			}];
		},

	}),
});

// }}}
// {{{ Report: core.queue-pauses

Reports.registerReport({
	id: 'core.queue-pauses',
	name: _('Report pauz'),

	render: () => ({

		expandable: true,
		groupColumn: 'user_id',
		
		renderChart(el, data, view) {
			el.textContent = '';
			if (view !== 'pauses') {
				return;
			}
			Charts.makeDoughnut(
				el,
				data.rows.map((row, i) => ({
					label: Formats.percent(row['ratio'], true) + ' - ' + row['pause_type_name'],
					color: Utils.getStyleRootProperty(`--pause-color-${i}`) || `hsl(${((i + 1) * 50) % 360}, 100%, 40%)`,
					value: row['ratio'],
				})),
				null,
				{},
				{
					responsive: true,
					aspectRatio: 2.3,
					maintainAspectRatio: true,
					animation: true,
					cutout: '50%',
					events: [],
					borderWidth: 2,
					borderColor: Utils.getStyleRootProperty('--panel-bg'),
					layout: {
						autoPadding: false,
						top: 0,
						bottom: 0,
						left: -10,
					},
					plugins: {
						legend: {
							fullSize: true,
							display: true,
							position: 'right',
							align: 'start',
							maxWidth: 240,
							labels: {
								textAlign: 'left',
								boxWidth: 16,
								boxHeight: 16,
								padding: 12,
								color: Utils.resolveCSSVar('var(--body-fg)'),
								usePointStyle: true,
								pointStyle: 'rectRounded',
								font: {
									family: 'Manrope',
									size: 16,
									weight: 500
								}
							}
						}
					}
				}, 
			);
		},
		
		'after:updateTableData'() {
			this.refs.toggleAllGroupsBtn.style.display = this.view === 'pauses' ? 'none' : 'block';
		},

		dataLoaded(data, view) {
			if (view === 'agents') {
				data.rows.forEach(row => {
					const rolled_up = !row['pause_type_name'];
					const groups = data.rows
						.filter(r => r['user_id'] == row['user_id'] && r['pause_type_name'])
						.map((r, i) => ({
							label: r['pause_type_name'],
							value: r['ratio'],
							color: rolled_up || r['pause_type_name'] == row['pause_type_name'] ?
								Utils.getStyleRootProperty(`--pause-color-${i}`) || `hsl(${((i + 1) * 50) % 360}, 100%, 40%)` : //
								'#ccc',
							duration: r['duration'], // for computing title only
						}));
					const row_groups = groups.filter(g => rolled_up || g['label'] == row['pause_type_name']); 
					row.chart_data = {
						groups,
						label: row_groups.map(g => Formats.percent(g['value'], true)),
						title: row_groups
							.map(g => `${Formats.percent(g['value'], true)} (${Formats.duration(g['duration'])}) - ${g['label']}`)
							.join('\n'),
					};
				});
			}
			return data;
		},

		getTableColumns(view) {
			return [view === 'agents' ? {
				id: 'user_name',
				name: _('Jméno'),
				formatCell(td, v, row) {
					if (!row['pause_type_name']) {
						td.gwCreateChild({textContent: row['user_name']});
					} else {
						td.textContent = row['pause_type_name'];
					}
				},
			} : {
				id: 'pause_type_name',
				name: _('Pauza'),
			}, {
				id: 'duration',
				format: 'duration',
				name: _('Trvání'),
			}, ...(view === 'agents' ? [{
				id: 'chart_data',
				name: _('Podíl'),
				formatCell(td, v, row) {
					if (row.chart_data) {
						const splittedLabel = row.chart_data.label.insertBetweenAll({ 
							nodeName: 'span', 
							className: 'text-separator', 
							textContent:" | "
						});
						Charts.makeDoughnut(
							td, 
							row.chart_data.groups, 
							splittedLabel
						);
						td.setAttribute('title', row.chart_data.title);
					}
				}
			}] : [{
				id: 'ratio',
				format: 'percent',
				name: _('Podíl'),
			}] )];
		},
	}),
});

// }}}
// {{{ Report: core.outgoing-calls-per-user-or-exten

Reports.registerReport({
	id: 'core.outgoing-calls-per-user-or-exten',
	name: _('Report odchozích hovorů'),

	render: () => ({

		getTableColumns(view) {
			return [view === 'user_name' ? {
				id: 'user_name',
				name: _('Jméno'),
				formatCell: (td, v) => {
					td.gwCreateChild({textContent: v})
				},
			} : {
				id: 'extension',
				name: _('Linka'),
			}, {
				id: 'count',
				name: _('Celkem'),
			}, {
				id: 'count_connected',
				name: _('Dovolané'),
			}, {
				id: 'count_not_connected',
				name: _('Nedovolané'),
			}, {
				id: 'avg_ring_duration',
				format: 'duration',
				name: _('⌀ Délka vyzvánění'),
			}, {
				id: 'avg_duration',
				format: 'duration',
				name: _('⌀ Délka hovoru'),
			}, {
				id: 'sum_duration',
				format: 'duration',
				name: _('∑ Délka hovorů'),
			}, {
				id: 'success_rate',
				name: _('Úspešnost dovolání'),
				formatCell: Reports.CellFormats.makeDoughnut(
					(td, v) => Formats.percent(v),
					[{
						label: _('Dovolané'),
						dataProperty: 'count_connected',
						color: 'var(--graph-success-color, green)',
					}, {
						label: _('Nedovolané'),
						dataProperty: 'count_not_connected',
						color: 'var(--graph-error-color, red)',
					}]
				),
			}];
		},
	}),
});

// }}}
// {{{ Report: core.call-counts-per-day-and-hour

Reports.registerReport({
	id: 'core.call-counts-per-day-and-hour',
	name: _('Měsíční report'),

	render: () => ({
		
		updateWrapped() {
			const expanded = this.refs.expander.classList.contains('expanded')
			let countOverscroll = 0;
			this.refs.queuesTabs.el.querySelectorAll('.item').forEach( i => {
				countOverscroll += + (i.offsetTop > 0);
			})
			if (countOverscroll) {
				this.refs.expander.classList.remove('hidden')
				this.refs.expander.textContent = expanded ? _('Méně') : `${_('Více')} (${countOverscroll})`;
			} else {
				this.refs.expander.classList.add('hidden')
				this.refs.expander.classList.remove('expanded')
			}
		},
		
		renderViews(views, onSelected, refs) {
			let queuesViews = views.filter( v => !['all', 'all_queues'].includes(v.id) );
			let me = this;
			return {
				setSelected(view) {
					if (view === 'all' || view === 'all_queues') {
						refs.queuesAllSelect.setValue(view);
						refs.queuesTabs.setSelected(null);
						refs.queuesAllSelect.el.classList.add('active', 'inverted');
					} else {
						refs.queuesAllSelect.setValue('all_queues')
						refs.queuesTabs.setSelected(view);
						refs.queuesAllSelect.el.classList.remove('active', 'inverted');

						const selectedQueueEl = refs.queuesTabs.el.querySelector('.item.active');
						if (selectedQueueEl && selectedQueueEl.offsetTop > 0) {
							refs.expander.classList.add('expanded');
						}
					}
					me.updateWrapped();
				},
				className: 'report-views-container',
				children: [{
					ref: 'queuesAllSelect',
					xtype: 'SelectField',
					cls: 'small',
					options: [{
						value: 'all',
						text: _('Všechny'),
					}, {
						value: 'all_queues',
						text: _('Všechny fronty'),
					}],
					'on:change': c => {
						onSelected(c.getValue());
					},
				}, {
					className: 'separator'
				}, {
					ref: 'expander',
					className: 'transparent icon-right small expander hidden',
					nodeName: 'button',
					type: 'button',
					'on:click': e => {
						e.currentTarget.classList.toggle('expanded');
						me.updateWrapped();
					},
				}, {
					ref: 'queuesTabs',
					xtype: 'Widgets.ButtonSwitcher',
					cls: "button-switcher small inverted",
					items: queuesViews,
					'on:beforeselect': (c, i) => {
						onSelected(i.id)
					},
				}],
			};
		},

		'after:initComponent'() {

			// Throttle function using requestAnimationFrame
			// https://github.com/wuct/raf-throttle/blob/master/rafThrottle.js
			const rafThrottle = callback => {
				let requestId = null
				let lastArgs

				const later = (context) => () => {
					requestId = null
					callback.apply(context, lastArgs)
				}

				const throttled = function (...args) {
					lastArgs = args;
					if (requestId === null) {
						requestId = requestAnimationFrame(later(this))
					}
				}

				return throttled
			}

			let hoveredElement;

			function updateCrossPositionVars(tdElement) {
				if (tdElement && tdElement.tagName && tdElement.tagName.toUpperCase() === "TD") {

					// Optimalization - Process each cell only once
					if (hoveredElement === tdElement) {
						return
					}
					hoveredElement = tdElement;

					const tr = tdElement.parentNode;
					const table = tdElement.parentNode.parentNode.parentNode;
					const tableScrollTop = table.parentNode.scrollTop;
					const tableHeight = table.clientHeight;
					const tableContainerHeight = table.parentNode.clientHeight;
					const headerHeight = table.firstChild.firstChild.clientHeight;

					let offsetTop = (tr.offsetTop - headerHeight - tableScrollTop) * -1;
					let offsetBottom = (tableContainerHeight - headerHeight - tr.clientHeight + offsetTop) * -1;

					// Safari Momentum bottom scroll overflow fix
					if (tableContainerHeight + tableScrollTop > tableHeight) {
						let negativeOffset = (tableContainerHeight + tableScrollTop) - tableHeight;
						offsetBottom = offsetBottom + negativeOffset;
					}

					table.style.setProperty('--table-hover-offset-top', offsetTop + 'px');
					table.style.setProperty('--table-hover-offset-bottom', offsetBottom + 'px');
				}
			}

			const updateCrossPositionVarsThrottled = rafThrottle(updateCrossPositionVars);

			function updateOnScroll(e) {
				let hoverTarget;
				hoverTarget = document.querySelector('td:hover');
				if (hoverTarget) {
					hoveredElement = null;
					updateCrossPositionVarsThrottled(hoverTarget);
				}
			}

			this.refs.table.el.classList.add('cross-hover');

			this.refs.table.el.firstChild.addEventListener('mousemove', (e) => updateCrossPositionVarsThrottled(e.target));

			this.refs.table.el.addEventListener('scroll', updateOnScroll);

			this.monDom(window, 'resize', () => this.updateWrapped(), null, false);
		},
		
		'after:setupFilters'() {
			this.setFilters(this.refs.viewsFilters, [Reports.Filters.options('type', [{
					id: 'all',
					title: _('Vše'),
				}, {
					id: 'in',
					title: _('Příchozí'),
				}, {
					id: 'out',
					title: _('Odchozí'),
				}, {
					id: 'missed',
					title: _('Zmeškané'),
				}]
			)], true);
		},

		getTableColumns() {
			const pad0 = n => String(n).padStart(2, '0');
			return [{
				id: 'date',
				format: 'date_weekday',
				name: _('Datum'),
			}, {
				id: 'total_count',
				name: _('Celkem'),
			}, ...[...Array(24).keys()].map(h => ({
				id: `h${h}`,
				formatCell: (td, v) => {
					td.textContent = v != 0 ? v : '';
				},
				name: {
					title: `${pad0(h)}:00 - ${pad0(h + 1)}:00`,
					children: [
						pad0(h),
						{nodeName: 'br'},
						pad0(h + 1),
					]
				}
			}))];
		},

	}),
});

// }}}
// {{{ Report: core.user-activity-per-day

Reports.registerReport({
	id: 'core.user-activity-per-day',
	name: _('Report aktivit'),

	render: () => ({

		getTableColumns() {
			return [{
				id: 'ts',
				format: 'date',
				name: _('Datum'),
			}, {
				id: 'ts',
				format: 'time',
				name: _('Čas'),
			}, {
				id: 'activity',
				name: _('Aktivita'),
			}, {
				id: 'name',
				name: _('ID'),
			}, {
				id: 'duration',
				format: 'duration',
				name: _('Trvání'),
			}];
		},

		renderViews(views, onSelected, refs) {
			return {
				xtype: 'SelectField',
				cls: "small light",
				options: views.map(r => {
					return {
						value: r.id,
						text: r.title,
					};
				}),
				setSelected(view) {
					this.setValue(view);
				},
				'on:change': async c => {
					onSelected(c.getValue());
				},
				filterable: true,
			};
		},	

	}),
});

// }}}
// {{{ Report: core.categories

Reports.registerReport({
	id: 'core.categories',
	name: _('Report kategorií'),

	// chart params, defines selected rows to be included in the chart
	chart: null,
	
	collapsed: false,
	
	render: () => ({

		getLocalStateKey(name, view) {
			return {
				user: Application.session.user.id,
				report: 'core.categories',
				view: view || this.view,
				name
			};
		},
		
		dataLoaded(data, view) {
			
			const reportThis = this;
			const buildFilter = name => {
				const options = data.filterOptions[name];
				if (!options) {
					return null;
				}
				
				const text_all = {
					'hierarchy_id': _('Všechny kategorie'),
					'queue': _('Všechny fronty'),
					'user_id': _('Všichni agenti'),
				}[name];
				
				const key = this.getLocalStateKey(name, view);

				const value = Utils.getLocalState(key);
				
				return {
					xtype: 'SelectField',
					cls: "small light",

					options: [{value: null, text: text_all}].concat(
						options.map(({id, name}) => ({value: id, text: name}))
					),
					placeholder: text_all,

					filterable: true,

					'on:change'() {
						const val = this.getValue();
						Utils.setLocalState(key, val);
						reportThis.reloadData();
					},

					'after:initComponent'() {
						this.setValue(value);
					},
				};
			};

			this.refs.categoriesFilters.gwReplaceChildren(({
				'categories': ['hierarchy_id', 'queue'],
				'queues': ['queue', 'hierarchy_id'],
				'agents': ['user_id', 'hierarchy_id', 'queue'],
			}[view]).map(buildFilter), this.categoriesFilterRefs);
			
			this.refs.viewToggleAllButton.gwHide(false);
			
			if (data.rows) {
				// setup chart to show automatically for the first row data:
				const firstRow = data.rows[0];
				let chart = null;
				if (firstRow) {
					if (!firstRow['parent_category_id']) {
						chart = {hierarchy_id: null};
					} else if (!firstRow['category_id']) {
						chart = {hierarchy_id: firstRow['hierarchy_id']};
					}
				}
				this.chart = chart;

				if (this.collapsed) {
					data.rows = data.rows
						.filter(row => !firstRow['parent_category_id'] ? !row['parent_category_id'] : !row['category_id']);
				}
			}
			return data;
		},
		
		// use URL search parameters even if the data is not yet loaded and filters not initialized via setFilters()
		'wrap:buildFilterData'(origFn, target) {
			const params = origFn(target);
			if (target !== 'xlsx') {
				for (const name of ['hierarchy_id', 'queue', 'user_id']) {
					params[name] = Utils.getLocalState(this.getLocalStateKey(name));
				}
				if (this.getAltContentText(params)) {
					params.get_rows = false;
				}
			}
			return params;
		},
		
		getAltContentText(filter) {
			if (this.view === 'queues' && !filter.queue) {
				return _('Vyber si frontu');
			}
			if (this.view === 'agents' && !filter.user_id) {
				return _('Vyber agenta');
			}
		},

		'wrap:updateTableData'(origFn) {
			const filter = this.buildFilterData();
			let altContentText = this.getAltContentText(filter);
			if (altContentText) {
				this.refs.altContent.gwReplaceChildren([{
					textContent: altContentText
				}]);
			}
			this.refs.table.el.gwHide(altContentText);
			if (this.refs.chart) {
				this.refs.chart.gwHide(altContentText);
			}
			if (this.refs.altContent) {
				this.refs.altContent.gwHide(!altContentText);
			}
			if (!altContentText) {
				origFn();
			}
		},
		
		renderViews(views, onSelected, refs) {
			return {
				setSelected(view) {
					refs.categoriesViews.setSelected(view);
				},
				children: [{
					ref: 'categoriesViews',
					xtype: 'Widgets.ButtonSwitcher',
					cls: "button-switcher small light",
					items: views,
					'on:beforeselect': (c, i) => {
						onSelected(i.id)
					},
				}, {
					className: 'separator',
				}, {
					ref: 'categoriesFilters',
					className: 'report-categories-filter'
				}, {
					ref: 'viewToggleAllButton',
					nodeName: 'button',
					type: 'button',
					className: 'secondary icon-left small hidden',
					children: !this.collapsed
						? [Utils.useIcon('collapse'), _('Sbalit vše')]
						: [Utils.useIcon('expand'), _('Rozbalit vše')],
					"on:click": () => {
						this.collapsed = !this.collapsed;
						refs.viewToggleAllButton.gwReplaceChildren(!this.collapsed
							? [Utils.useIcon('collapse'), _('Sbalit vše')]
							: [Utils.useIcon('expand'), _('Rozbalit vše')]
						);
						this.reloadData(true);
					},
				}]
			};
		},
		
		getTableColumns: function (view) {
			return [{
				id: 'name',
				name: 'Kategorie',
				formatCell: (td, v, row) => {
					td.classList.add('text-pre');
					if (row['parent_category_id'] === null && row['category_id'] === null) {
						td.textContent = row['hierarchy_name'];
					} else if (row['parent_category_id'] !== null && row['category_id'] === null) {
						td.textContent = '    ' + row['parent_category_name'];
					} else {
						td.textContent = '        ' + row['category_name'];
					}
					td.width = '100%';
				}
			}, {
				id: 'count',
				name: _('Výskyt'),
			}, {
				id: 'ratio',
				name: _('Podíl'),
				format: 'percent',
			}]
		},
		
		getRowFilterToChart() {
			const {hierarchy_id} = this.chart || {};
			if (hierarchy_id) {
				// parent category level
				return row => row['hierarchy_id'] == hierarchy_id && row['parent_category_id'] && !row['category_id'];
			} else {
				// top level
				return row => !row['parent_category_id'] && !row['category_id'];
			}
		},

		renderChart(el, data, view) {
			el.textContent = '';
			
			let totalValue = 0;
			let dataFiltered = data.rows.filter(this.getRowFilterToChart()).map((row, i) => {
				totalValue += parseFloat(row['ratio']);
				return {
					label: Formats.percent(row['ratio']) + ' - ' + 
						(row['category_name'] || row['parent_category_name'] || row['hierarchy_name']),
					color: `hsl(${((i + 1) * 50) % 360}, 100%, 40%)`,
					value: row['ratio'],
				};
			});

			// When total amount of graph values equals zero (which actually means empty doughnut graph) we generate extra "empty" dataset which shows "empty" graph (and hides it from the legend)
			if (totalValue === 0) {
				dataFiltered.push({label: '###HIDDEN-LABEL###', color: Utils.getStyleRootProperty('--body-bg'), value: 1})
			}
			
			// https://stackoverflow.com/a/42809704
			function linearRemap(value, oldMin, oldMax, newMin, newMax) {
				var newScale = newMax - newMin;
				var valueAsPct = (value - oldMin) / (oldMax - oldMin);
				var scaledValue = valueAsPct * newScale;
				var shiftedAndScaledValue = scaledValue + newMin;
				return shiftedAndScaledValue;
			}
			
			Charts.makeDoughnut(
				el,
				dataFiltered,
				null,
				{},
				{
					responsive: true,
					aspectRatio: linearRemap(dataFiltered.length, 8, 14, 2.3, 1.5),
					maintainAspectRatio: true,
					animation: true,
					cutout: '50%',
					events: [],
					borderWidth: 2,
					borderColor: Utils.getStyleRootProperty('--panel-bg'),
					layout: {
						autoPadding: false,
					},
					plugins: {
						legend: {
							fullSize: true,
							display: true,
							position: 'right',
							align: 'center',
							maxWidth: 280,
							labels: {
								textAlign: 'left',
								boxWidth: 16,
								boxHeight: 16,
								padding: linearRemap(dataFiltered.length, 8, 15, 12, 6),
								color: Utils.resolveCSSVar('var(--body-fg)'),
								usePointStyle: true,
								pointStyle: 'rectRounded',
								font: {
									family: 'Manrope',
									size: 16,
									weight: 500
								},
								filter: function(item, chart) {
									return item.text !== '###HIDDEN-LABEL###';
								}
							}
						}
					}
				}
			);
		},

	}),
});

// }}}
// {{{ Report: core.outbound_campaigns

Reports.registerReport({
	id: 'core.outbound-campaigns',
	name: _('Report kampaní'),

	render: () => ({

		expandable: true,

		getTableColumns(view) {
			return [view === 'agent' ? {
				id: 'user_id',
				name: _('Agent'),
				formatCell(td, v, row) {
					if (row._expandable.topLevel) {
						td.textContent = row['user_name'];
					} else {
						td.textContent = row['campaign_name'];
					}
				},
			} : {
				id: 'campaign_id',
				name: _('Kampaň'),
				formatCell(td, v, row) {
					if (row._expandable.topLevel) {
						td.textContent = row['campaign_name'];
					} else {
						td.textContent = row['user_name'];
					}
				},
			}, {
				id: 'calls_count',
				name: _('Počet zavolání'),
			}, {
				id: 'result_success_count',
				name: _('Počet pozitivních zakončení'),
			}, {
				id: 'result_fail_count',
				name: _('Počet negativních zakončení')
			},{
				id: 'chart',
				name: _('Graf uzavření případů'),
				formatCell(td, v, row) {
					const groups = [row['not_closed_count'] !== null ? [{
						label: _('Neuzavřené případy'),
						dataProperty: 'not_closed_count',
						color: 'var(--graph-border-color, gray)',
					}] : [], {
						label: _('Pozitivní uzavření'),
						dataProperty: 'result_success_count',
						color: 'var(--graph-success-color, green)',
					}, {
						label: _('Negativní uzavření'),
						dataProperty: 'result_fail_count',
						color: 'var(--graph-error-color, red)',
					}].flat();
					return Reports.CellFormats.makeDoughnut('allAsPercentWithLabels', groups)(td, v, row);
				},
			}];
		},

	}),
});

// }}}

Application.addRoutes([{
	group: 'auth',
	module: 'reports',
	path: APP_BASE + 'reports',
	async handler() {
		let c = Application.show({
			id: 'reports.main',
			xtype: 'Reports.OverviewScreen',
		});
		c.applyParams({selectedReportId: Reports.reports[0] ? Reports.reports[0].id : null});
	}
}, {
	group: 'auth',
	module: 'reports',
	path: APP_BASE + 'reports/{report:str}',
	async handler({params, captures}) {
		let c = Application.show({
			id: 'reports.main',
			xtype: 'Reports.OverviewScreen',
		});
		c.applyParams({selectedReportId: captures.report});
	}
}]);

Application.registerModule({
	id: 'reports',
	title: _('Reporty'),
	tagline: _('Reporty a statistiky kontaktního centra'),
	icon: 'chart',
	href: APP_BASE + 'reports/',
});
